/**
 * @file content-reaction.schema.ts
 * @description Schema Zod para a entidade 'ContentReaction'.
 * Cada documento representa a reação (LIKE/DISLIKE) de um usuário em um conteúdo
 * específico (vídeo de curso ou revista). Há no máximo uma reação por
 * (userId, contentType, contentId) — uma nova reação atualiza a existente.
 *
 * Esta coleção alimenta métricas de qualidade do conteúdo no dashboard admin.
 * Está preparada para receber feedback textual opcional no futuro via o campo
 * `comment`.
 */

import { z } from 'zod';

// --- Enums ---

/**
 * Tipos de conteúdo que aceitam reação do usuário.
 * - VIDEO: aula individual de um curso (identificada por filename ou id do vídeo)
 * - MAGAZINE: revista (identificada pelo id da revista)
 */
const ReactionContentTypeEnum = z.enum(['VIDEO', 'MAGAZINE']);

/**
 * Tipo da reação do usuário.
 */
const ReactionTypeEnum = z.enum(['LIKE', 'DISLIKE']);

// --- Main Schema ---

const ContentReactionSchema = z.object({
  id: z.string(),

  // Identificação do usuário e contexto
  userId: z.string(),
  pregnancyId: z.string().nullable(),
  partnerId: z.string().nullable(),

  // Identificação do conteúdo
  contentType: ReactionContentTypeEnum,
  contentId: z.string(),
  // Para reações em VIDEO, guardamos o courseId para facilitar agregações por curso
  courseId: z.string().nullable(),

  // Reação (LIKE/DISLIKE) — mutável (pode trocar e remover via toggle).
  // Pode ser null se o usuário comentou e depois removeu a reação.
  reaction: ReactionTypeEnum.nullable(),

  // Feedback textual opcional (max 2000 chars). One-shot: imutável após o primeiro envio.
  comment: z.string().max(2000).nullable(),

  // Auditoria
  createdAt: z.date(),
  updatedAt: z.date(),
});


// --- Inferred TypeScript Types ---

type ContentReaction = z.infer<typeof ContentReactionSchema>;
type ReactionContentType = z.infer<typeof ReactionContentTypeEnum>;
type ReactionType = z.infer<typeof ReactionTypeEnum>;


// --- Public Exports ---

export {
  ContentReactionSchema,
  ReactionContentTypeEnum,
  ReactionTypeEnum,
};

export type {
  ContentReaction,
  ReactionContentType,
  ReactionType,
};
